import ErrorBoundary from './components/ErrorBoundary';

import React from 'react';
import './styles/App.css';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams, Outlet } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import OfflineAwareWrapper from './components/OfflineAwareWrapper';
import { AuthProvider, ProtectedRoute } from './context/AuthContext';
import { NavigationGuardProvider, GlobalNavigationGuard } from './context/NavigationGuardContext';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import { SovereignStateProvider } from './context/SovereignStateContext';
import { filterChroniclesByLanguage } from './utils/carrierResolver';

import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo } from 'react';
import { fetchChronicles } from './store/heroSlice';
import { resolveApiBaseUrl } from './config/apiConfig';
import { useAudioPlayerContext } from './context/AudioPlayerProvider';
import StoneLayout from './components/layout/StoneLayout';
import SkipNavLink from './components/SkipNavLink';
import Footer from './components/Footer';
import ScrollManager from './components/ScrollManager';
import { CanvasProvider } from './context/CanvasProvider';
import UniverseLayout from './layouts/UniverseLayout';
import PassportLayout from './layouts/PassportLayout';
import PassportErrorBoundary from './components/errors/PassportErrorBoundary';
import MatrixRouteBoundary from './matrix/MatrixRouteBoundary';
import { lazyWithReload } from './utils/lazyWithReload';
import { getArtistProfile } from './data/artistProfiles';
import { resolveVenueProfile } from './data/venueProfiles';
import SignalLostNotice from './components/SignalLostNotice';

// Route-level components stay lazy so direct links do not download unrelated surfaces.
const HomePage = lazyWithReload(() => import('./pages/FieldHomePage'), 'home');
const SignalCardLanding = React.lazy(() => import('./pages/SignalCardLanding'));
const Blog = React.lazy(() => import('./pages/Blog'));
const BlogPostRoute = React.lazy(() => import('./pages/BlogPostRoute'));
const Portfolio = React.lazy(() => import('./pages/Portfolio'));
const About = React.lazy(() => import('./pages/About'));
const ManifestoPage = React.lazy(() => import('./pages/ManifestoPage'));
const FieldArchivePage = lazyWithReload(() => import('./pages/FieldArchivePage'), 'field-archive');
const Radio = React.lazy(() => import('./pages/Radio'));
const BlackStarSignal = lazyWithReload(() => import('./pages/BlackStarSignal'), 'black-star-signal');
const Communications = lazyWithReload(() => import('./pages/Communications'), 'communications');
const CommunicationsRouteResolver = lazyWithReload(() => import('./pages/Communications').then((module) => ({ default: module.CommunicationsRouteResolver })), 'communications-resolver');
const BBCNews = React.lazy(() => import('./components/BBCNews'));
const AggregatedNews = React.lazy(() => import('./components/AggregatedNews'));
const LessonPlanner = React.lazy(() => import('./pages/LessonPlanner'));
const Login = lazyWithReload(() => import('./pages/Login'), 'login');
const NewsPost = React.lazy(() => import('./components/NewsPost'));
const MemoryDashboard = React.lazy(() => import('./components/admin/MemoryDashboard'));
const MultiStepSignUp = React.lazy(() => import('./components/MultiStepSignUp'));
const AddEvent = React.lazy(() => import('./components/AddEvent'));
const UserRoleManager = React.lazy(() => import('./components/UserRoleManager'));
const GigPassport = lazyWithReload(() => import('./components/GigPassport'), 'gig-passport');
const GigPassportGateway = lazyWithReload(() => import('./components/GigPassport/GigPassportGateway'), 'gig-passport-gateway');
const CanvasRoomTestHarness = React.lazy(() => import('./pages/CanvasRoomTestHarness').then(module => ({ default: module.CanvasRoomTestHarness })));
const LessonRoomTestHarness = React.lazy(() => import('./pages/MockLessonRoom').then(module => ({ default: module.LessonRoomTestHarness })));
const GigPromoRoomTestHarness = React.lazy(() => import('./pages/MockGigPromoRoom').then(module => ({ default: module.GigPromoRoomTestHarness })));
const ArtMenuGenerator = React.lazy(() => import('./pages/ArtMenuGenerator'));
const CreativeLab = React.lazy(() => import('./pages/CreativeLab'));
const BringAnyIdeaPage = React.lazy(() => import('./pages/BringAnyIdeaPage'));
const RadioChronicleChallenge = React.lazy(() => import('./pages/RadioChronicleChallenge'));
const Archives = React.lazy(() => import('./pages/Archives'));
const Profile = React.lazy(() => import('./pages/Profile'));
const Ledger = React.lazy(() => import('./pages/Ledger'));
const JournalPage = React.lazy(() => import('./pages/JournalPage'));
const ProducerQueuePage = React.lazy(() => import('./pages/ProducerQueuePage'));
const HandoffDraftPage = React.lazy(() => import('./pages/admin/HandoffDraftPage'));
const ChronicleDraftInboxPage = React.lazy(() => import('./pages/admin/ChronicleDraftInboxPage'));
const ChronicleDraftEditorPage = React.lazy(() => import('./pages/admin/ChronicleDraftEditorPage'));
const LessonSeedInboxPage = React.lazy(() => import('./pages/admin/LessonSeedInboxPage'));
const LessonSeedEditorPage = React.lazy(() => import('./pages/admin/LessonSeedEditorPage'));
const JournalPromptInboxPage = React.lazy(() => import('./pages/admin/JournalPromptInboxPage'));
const JournalPromptEditorPage = React.lazy(() => import('./pages/admin/JournalPromptEditorPage'));
const WordplaySeedInboxPage = React.lazy(() => import('./pages/admin/WordplaySeedInboxPage'));
const WordplaySeedEditorPage = React.lazy(() => import('./pages/admin/WordplaySeedEditorPage'));
const StudioOverviewPage = React.lazy(() => import('./pages/admin/StudioOverviewPage'));
const StudioOperatorGuidePage = React.lazy(() => import('./pages/admin/StudioOperatorGuidePage'));
const StudioAcceptancePage = React.lazy(() => import('./pages/admin/StudioAcceptancePage'));
const MatrixControlRoomPage = React.lazy(() => import('./pages/admin/MatrixControlRoomPage'));
const MontrealJazzBriefPage = React.lazy(() => import('./pages/admin/MontrealJazzBriefPage'));
const MontrealJazzBriefsIndexPage = React.lazy(() => import('./pages/admin/MontrealJazzBriefsIndexPage'));
const MontrealJazzBrief = React.lazy(() => import('./pages/MontrealJazzBrief'));
const MontrealJazzDossierPage = React.lazy(() => import('./pages/MontrealJazzDossierPage'));
const Harlem147thPortal = React.lazy(() => import('./pages/Harlem147thPortal'));
const RxAfricaInvestorPortal = React.lazy(() => import('./pages/RxAfricaInvestorPortal'));
const ProjectEvidenceAdminPage = React.lazy(() => import('./pages/admin/ProjectEvidenceAdminPage'));
const KismatLandingPage = React.lazy(() => import('./pages/KismatLandingPage'));
const ArtistProfilePage = React.lazy(() => import('./pages/ArtistProfilePage'));
const VenueProfilePage = React.lazy(() => import('./pages/VenueProfilePage'));
const FieldTransmissionPage = lazyWithReload(() => import('./pages/FieldTransmissionPage'), 'passport-field-transmission');
const UnauthorizedPage = React.lazy(() => import('./pages/UnauthorizedPage'));
const FieldScannerLanding = lazyWithReload(() => import('./components/GigPassport/components/FieldScannerLanding'), 'passport-field-scanner');
const StaffScannerLanding = lazyWithReload(() => import('./components/GigPassport/components/StaffScannerLanding'), 'passport-staff-scanner');
const SignalRadarFeed = lazyWithReload(() => import('./components/GigPassport/components/SignalRadarFeed'), 'passport-signal-radar');
const OperatorLedgerDashboard = React.lazy(() => import('./components/admin/OperatorLedgerDashboard'));
const ResearchDesk = React.lazy(() => import('./components/admin/ResearchDesk'));
const QrGeneratorPage = React.lazy(() => import('./pages/admin/QrGeneratorPage'));
const StoryEditorPage = React.lazy(() => import('./pages/admin/StoryEditorPage'));
const ArtistQrScanRoute = React.lazy(() => import('./components/GigPassport/components/ArtistQrScanRoute'));
const ArtistDashboardPage = React.lazy(() => import('./pages/ArtistDashboardPage'));
const TogeeVisionPreview = React.lazy(() => import('./pages/TogeeVisionPreview'));
const StartLandingPage = lazyWithReload(() => import('./pages/StartLandingPage'), 'start-landing');
const FieldLandingPage = lazyWithReload(() => import('./pages/FieldLandingPage'), 'field-landing');
const StoryLandingPage = lazyWithReload(() => import('./pages/StoryLandingPage'), 'story-landing');
const FieldNotesPage = lazyWithReload(() => import('./pages/FieldNotesPage'), 'field-notes');
const ConstellationPage = React.lazy(() => import('./pages/ConstellationPage'));
const PlacePage = React.lazy(() => import('./pages/PlacePage'));
const SignalDestinationPage = React.lazy(() => import('./pages/SignalDestinationPage'));
const ScanRoute = React.lazy(() => import('./pages/ScanRoute'));
const LegacyCardRoute = React.lazy(() => import('./pages/LegacyCardRoute'));

const CanvasWorkspace = lazyWithReload(() => import('./pages/passport/CanvasWorkspace'), 'passport-canvas');
const GigPassportOnboarding = lazyWithReload(() => import('./components/GigPassport/components/GigPassportOnboarding'), 'passport-onboarding');

// Centralized theme imported from ./theme.js

// AdminRedirect helper component to preserve queries while redirecting to tabbed cockpit
const AdminRedirect = ({ toTab }) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set('tab', toTab);
  return <Navigate to={`/operator/ledger?${params.toString()}`} replace />;
};

// SpotlightRedirect component to resolve scan params and redirect to canonical scan resolver
const SpotlightRedirect = () => {
  const { handle } = useParams();
  const location = useLocation();
  const cleanId = handle === 'monk-americana' ? 'monk-washington' : (handle?.replace(/^artist-/, '') || handle);
  const artist = getArtistProfile(cleanId);
  if (artist) {
    return <Navigate to={`/scan/artist/${cleanId}${location.search}`} replace />;
  }
  const venue = resolveVenueProfile(handle);
  if (venue && !venue.isFallback) {
    return <Navigate to={`/scan/venue/${venue.id}${location.search}`} replace />;
  }
  return <Navigate to={`/passport/scan/${handle}${location.search}`} replace />;
};

const NODE_CHRONICLE_SLUGS = new Set([
  'harlem-505-w147-studio',
  'node-harlem-505-w147-studio',
  'venue-harlem-505-w147-studio',
  'coordinate-505',
  '505-west-147th',
  '503-505-west-147th',
  'plato-clock',
]);

// CityRadarRedirect preserves permanent public city URLs (/montreal, /nyc, /cities/:cityId, etc.)
// while resolving to the single canonical Signal Radar hub (/passport/radar?city=...),
// or to a fixed Node chronicle when /nodes/:slug addresses a canonical Node coordinate.
const CityRadarRedirect = ({ fixedCityId }) => {
  const { cityId: paramCityId } = useParams();
  const location = useLocation();
  const rawCity = (fixedCityId || paramCityId || 'nyc').toLowerCase().trim();
  if (!fixedCityId && NODE_CHRONICLE_SLUGS.has(rawCity)) {
    return <Navigate to={`/passport/transmissions/node-harlem-505-w147-studio${location.search}`} replace />;
  }
  const normalizedCity = rawCity === 'lehigh_valley' || rawCity === 'lvpa'
    ? 'lehigh-valley'
    : rawCity === 'roc'
    ? 'rochester'
    : rawCity;
  const params = new URLSearchParams(location.search);
  params.set('city', normalizedCity);
  return <Navigate to={`/passport/radar?${params.toString()}`} replace />;
};

// RadarFeedRedirect component to resolve radar feed and redirect to sub-route
const RadarFeedRedirect = () => {
  const location = useLocation();
  return <Navigate to={`/passport/radar${location.search}`} replace />;
};

// FieldRedirect component to resolve field page and redirect to sub-route
const FieldRedirect = () => {
  const { fieldId } = useParams();
  const location = useLocation();
  return <Navigate to={`/passport/transmissions/${fieldId}${location.search}`} replace />;
};

const QueryRedirect = ({ to }) => {
  const location = useLocation();
  const parts = to.split('?');
  const base = parts[0];
  const toParams = new URLSearchParams(parts[1] || '');
  const locParams = new URLSearchParams(location.search);
  
  for (const [key, value] of locParams.entries()) {
    if (!toParams.has(key)) {
      toParams.set(key, value);
    }
  }
  const searchStr = toParams.toString();
  return <Navigate to={`${base}${searchStr ? '?' + searchStr : ''}`} replace />;
};

const ScanRedirect = () => {
  const { id } = useParams();
  const location = useLocation();
  return <Navigate to={`/passport/scan/${id}${location.search}`} replace />;
};

const StaffRedirect = () => {
  const { handle } = useParams();
  const location = useLocation();
  return <Navigate to={`/passport/staff/${handle}${location.search}`} replace />;
};

const TransmissionRedirect = () => {
  const { fieldId } = useParams();
  const location = useLocation();
  return <Navigate to={`/passport/transmissions/${fieldId}${location.search}`} replace />;
};

// Route configuration with access control
const routes = [
  { path: "/", element: <HomePage /> },
  { path: "/radio", element: <Radio /> },
  { path: "/radar", element: <RadarFeedRedirect />, requiresAuth: false },
  { path: "/radar/spotlight/:handle", element: <SpotlightRedirect />, requiresAuth: false },
  { path: "/gig-passport/staff/:handle", element: <StaffScannerLanding />, requiresAuth: false },
  { path: "/radar/feed", element: <RadarFeedRedirect />, requiresAuth: false },
  { path: "/admin/cockpit", element: <OperatorLedgerDashboard />, requiresAuth: true, requiredRole: ['admin', 'operator', 'radar_operator'] },
  { path: "/admin/qr", element: <QrGeneratorPage />, requiresAuth: true, requiredRole: ['admin', 'operator', 'radar_operator'] },
  { path: "/operator/qr", element: <QrGeneratorPage />, requiresAuth: true, requiredRole: ['admin', 'operator', 'radar_operator'] },
  { path: "/admin/story", element: <StoryEditorPage />, requiresAuth: true, requiredRole: ['admin', 'operator'] },
  { path: "/admin/story/:fragmentId", element: <StoryEditorPage />, requiresAuth: true, requiredRole: ['admin', 'operator'] },
  { path: "/operator/story", element: <StoryEditorPage />, requiresAuth: true, requiredRole: ['admin', 'operator'] },
  { path: "/operator/story/:fragmentId", element: <StoryEditorPage />, requiresAuth: true, requiredRole: ['admin', 'operator'] },
  { path: "/qr", element: <QrGeneratorPage />, requiresAuth: false },
  // Dedicated Tune In route renders broadcast player page
  { path: "/black-star-signal", element: <BlackStarSignal /> },
  { path: "/field-archive", element: <FieldArchivePage />, requiresAuth: false },
  { path: "/archive", element: <FieldArchivePage />, requiresAuth: false },
  { path: "/scan", element: <ScanRoute />, requiresAuth: false },
  { path: "/signal-card", element: <LegacyCardRoute />, requiresAuth: false },
  { path: "/card", element: <LegacyCardRoute />, requiresAuth: false },
  { path: "/qr-card", element: <LegacyCardRoute />, requiresAuth: false },
  { path: "/story", element: <ErrorBoundary componentName="StoryLandingPage"><StoryLandingPage /></ErrorBoundary>, requiresAuth: false },
  { path: "/the-story", element: <Navigate to="/story" replace />, requiresAuth: false },
  { path: "/season-1", element: <Navigate to="/story" replace />, requiresAuth: false },
  { path: "/narrative", element: <Navigate to="/story" replace />, requiresAuth: false },
  { path: "/login", element: <Login /> },
  { path: "/signup", element: <MultiStepSignUp /> },
  { path: "/blog", element: <Blog />, requiresAuth: false },
  { path: "/blog/:title", element: <BlogPostRoute />, requiresAuth: false },
  { path: "/portfolio", element: <Portfolio />, requiresAuth: true },
  { path: "/portfolio/:title", element: <Portfolio />, requiresAuth: true },
  { path: "/about", element: <About />, requiresAuth: false },
  { path: "/archives", element: <Archives />, requiresAuth: true },
  // Alias to keep legacy and external links working (e.g., Beehiiv): /chronicles → Archives
  { path: "/chronicles", element: <Archives />, requiresAuth: true },
  { path: "/ledger", element: <Ledger />, requiresAuth: false },
  { path: "/journal", element: <JournalPage />, requiresAuth: true },
  {
    path: "/communications",
    element: <ErrorBoundary componentName="Communications"><CommunicationsRouteResolver /></ErrorBoundary>,
    requiresAuth: false,
  },
  {
    path: "/communications/:mode",
    element: <ErrorBoundary componentName="Communications"><Communications /></ErrorBoundary>,
    requiresAuth: false,
  },
  {
    path: "/communications/*",
    element: <ErrorBoundary componentName="Communications"><CommunicationsRouteResolver /></ErrorBoundary>,
    requiresAuth: false,
  },
  { path: "/contact", element: <Navigate to="/communications/transmit" replace />, requiresAuth: false },
  { path: "/admin/memory", element: <MemoryDashboard />, requiresAuth: true },
  { path: "/admin/producer", element: <ProducerQueuePage />, requiresAuth: true },
  { path: "/admin/handoffs/:destination/:draftId", element: <HandoffDraftPage />, requiresAuth: true },
  { path: "/admin/chronicle/drafts", element: <ChronicleDraftInboxPage />, requiresAuth: true },
  { path: "/admin/chronicle/drafts/:draftId", element: <ChronicleDraftEditorPage />, requiresAuth: true },
  { path: "/admin/lesson-plans/seeds", element: <LessonSeedInboxPage />, requiresAuth: true },
  { path: "/admin/lesson-plans/seeds/:seedId", element: <LessonSeedEditorPage />, requiresAuth: true },
  { path: "/admin/journal/prompts", element: <JournalPromptInboxPage />, requiresAuth: true },
  { path: "/admin/journal/prompts/:promptId", element: <JournalPromptEditorPage />, requiresAuth: true },
  { path: "/admin/creative-lab/wordplay", element: <WordplaySeedInboxPage />, requiresAuth: true },
  { path: "/admin/creative-lab/wordplay/:seedId", element: <WordplaySeedEditorPage />, requiresAuth: true },
  { path: "/admin/studio/overview", element: <StudioOverviewPage />, requiresAuth: true },
  { path: "/admin/studio/overview/:queueId", element: <StudioOverviewPage />, requiresAuth: true },
  { path: "/admin/montreal-jazz/briefs", element: <MontrealJazzBriefsIndexPage />, requiresAuth: true },
  { path: "/admin/montreal-jazz/briefs/:briefId", element: <MontrealJazzBriefPage />, requiresAuth: true },
  { path: "/admin/studio/guide", element: <StudioOperatorGuidePage />, requiresAuth: true },
  { path: "/admin/studio/acceptance", element: <StudioAcceptancePage />, requiresAuth: true },
  { path: "/admin/control-room", element: <MatrixControlRoomPage />, requiresAuth: true },
  { path: "/admin/radar", element: <AdminRedirect toTab="radar" />, requiresAuth: true, requiredRole: ['admin', 'operator', 'radar_operator'] },
  { path: "/unauthorized", element: <UnauthorizedPage />, requiresAuth: true },
  { path: "/admin/llm", element: <AdminRedirect toTab="llm" />, requiresAuth: true, requiredRole: ['admin', 'operator', 'radar_operator'] },
  { path: "/admin/llc", element: <AdminRedirect toTab="llm" />, requiresAuth: true, requiredRole: ['admin', 'operator', 'radar_operator'] },
  { path: "/togeevision/admin/llm", element: <AdminRedirect toTab="llm" />, requiresAuth: true, requiredRole: ['admin', 'operator', 'radar_operator'] },
  { path: "/togeevision/admin/llc", element: <AdminRedirect toTab="llm" />, requiresAuth: true, requiredRole: ['admin', 'operator', 'radar_operator'] },
  { path: "/mtl", element: <MontrealJazzBrief />, requiresAuth: false },
  { path: "/montreal-jazz", element: <MontrealJazzBrief />, requiresAuth: false },
  { path: "/montreal-jazz-collab", element: <MontrealJazzBrief />, requiresAuth: false },
  { path: "/montreal-jazz-dossier", element: <MontrealJazzDossierPage />, requiresAuth: false },
  { path: "/147th", element: <ErrorBoundary componentName="Harlem147thPortal"><Harlem147thPortal /></ErrorBoundary>, requiresAuth: false },
  { path: "/harlem-147th", element: <ErrorBoundary componentName="Harlem147thPortal"><Harlem147thPortal /></ErrorBoundary>, requiresAuth: false },
  { path: "/147th-street", element: <ErrorBoundary componentName="Harlem147thPortal"><Harlem147thPortal /></ErrorBoundary>, requiresAuth: false },
  { path: "/rxafrica", element: <ErrorBoundary componentName="RxAfricaInvestorPortal"><RxAfricaInvestorPortal /></ErrorBoundary>, requiresAuth: false },
  { path: "/rx-africa", element: <ErrorBoundary componentName="RxAfricaInvestorPortal"><RxAfricaInvestorPortal /></ErrorBoundary>, requiresAuth: false },
  { path: "/africa-rx", element: <ErrorBoundary componentName="RxAfricaInvestorPortal"><RxAfricaInvestorPortal /></ErrorBoundary>, requiresAuth: false },
  { path: "/admin/evidence", element: <ErrorBoundary componentName="ProjectEvidenceAdminPage"><ProjectEvidenceAdminPage /></ErrorBoundary>, requiresAuth: true, requiredRole: ['admin', 'operator'] },
  { path: "/admin/project-evidence", element: <ErrorBoundary componentName="ProjectEvidenceAdminPage"><ProjectEvidenceAdminPage /></ErrorBoundary>, requiresAuth: true, requiredRole: ['admin', 'operator'] },
  { path: "/operator/evidence", element: <ErrorBoundary componentName="ProjectEvidenceAdminPage"><ProjectEvidenceAdminPage /></ErrorBoundary>, requiresAuth: true, requiredRole: ['admin', 'operator'] },
  // Permanent public city aliases -> canonical Signal Radar hub (/passport/radar?city=...)
  { path: "/nyc", element: <CityRadarRedirect fixedCityId="nyc" />, requiresAuth: false },
  { path: "/montreal", element: <CityRadarRedirect fixedCityId="montreal" />, requiresAuth: false },
  { path: "/rochester", element: <CityRadarRedirect fixedCityId="rochester" />, requiresAuth: false },
  { path: "/roc", element: <CityRadarRedirect fixedCityId="rochester" />, requiresAuth: false },
  { path: "/rtm", element: <CityRadarRedirect fixedCityId="rtm" />, requiresAuth: false },
  { path: "/laurentian", element: <CityRadarRedirect fixedCityId="laurentian" />, requiresAuth: false },
  { path: "/lehigh-valley", element: <CityRadarRedirect fixedCityId="lehigh-valley" />, requiresAuth: false },
  { path: "/lvpa", element: <CityRadarRedirect fixedCityId="lehigh-valley" />, requiresAuth: false },
  { path: "/cities/:cityId", element: <CityRadarRedirect />, requiresAuth: false },
  { path: "/nodes/:cityId", element: <CityRadarRedirect />, requiresAuth: false },
  { path: "/start", element: <ErrorBoundary componentName="StartLandingPage"><StartLandingPage /></ErrorBoundary>, requiresAuth: false },
  { path: "/field-notes", element: <FieldNotesPage />, requiresAuth: false },
  { path: "/field-notes/:id", element: <FieldNotesPage />, requiresAuth: false },
  { path: "/constellations/:id", element: <ConstellationPage />, requiresAuth: false },
  { path: "/places/:venueId", element: <PlacePage />, requiresAuth: false },
  { path: "/venues/:venueId", element: <VenueProfilePage />, requiresAuth: false },
  { path: "/:city/venues/:slug", element: <SignalDestinationPage kind="venue" />, requiresAuth: false },
  { path: "/:city/signals/:slug", element: <SignalDestinationPage kind="artist" />, requiresAuth: false },
  { path: "/kismat", element: <KismatLandingPage />, requiresAuth: false },
  // Canonical physical QR scan resolvers
  { path: "/scan/artist/:artistSlug", element: <ArtistQrScanRoute entityType="artist" />, requiresAuth: false },
  { path: "/scan/venue/:venueSlug", element: <ArtistQrScanRoute entityType="venue" />, requiresAuth: false },
  { path: "/scan/gig/:eventId", element: <ArtistQrScanRoute entityType="gig" />, requiresAuth: false },
  { path: "/scan/:artistSlug", element: <ArtistQrScanRoute />, requiresAuth: false },
  { path: "/artists/:profileId", element: <ArtistProfilePage />, requiresAuth: false },
  { path: "/artists/:artistSlug/dashboard", element: <ArtistDashboardPage />, requiresAuth: true },
  { path: "/fields/:fieldId", element: <FieldRedirect />, requiresAuth: false },
  { path: "/news", element: <AggregatedNews /> },
  { path: "/aggregated-news", element: <AggregatedNews /> },
  { path: "/bbc-news", element: <BBCNews /> },
  { path: "/news/:title", element: <NewsPost /> },
  { path: "/add-event", element: <AddEvent />, requiresAuth: true },
  { path: "/manage-role", element: <UserRoleManager />, requiresAuth: true },
  { path: "/lesson-planner", element: <LessonPlanner />, requiresAuth: true },
  { path: "/sheet-genius", element: <LessonPlanner />, requiresAuth: true }, // Alias for About page Featured Tools
  { path: "/gig-passport", element: <GigPassportGateway />, requiresAuth: false },
  { path: "/gig-passport/app", element: <GigPassport view="app" />, requiresAuth: false },
  { path: "/gig-passport/coordinate", element: <GigPassport view="coordinate" />, requiresAuth: true },
  { path: "/gig-passport/cities", element: <GigPassport view="cities" />, requiresAuth: false },
  { path: "/gig-passport/signal", element: <GigPassport view="signal" />, requiresAuth: false },
  { path: "/gig-passport/me", element: <GigPassport view="passport" />, requiresAuth: false },
  { path: "/gig-passport/passport", element: <GigPassport view="passport" />, requiresAuth: false },
  { path: "/gig-passport/events", element: <GigPassport view="events" />, requiresAuth: false },
  { path: "/gig-passport/venue/kismat", element: <GigPassport view="venue" />, requiresAuth: false },
  { path: "/gig-passport/signal-check", element: <GigPassport view="signal-check" />, requiresAuth: false },
  { path: "/gig-passport/broadcast", element: <GigPassport view="broadcast" />, requiresAuth: false },
  { path: "/gig-passport/scan/:id", element: <FieldScannerLanding />, requiresAuth: false },
  { path: "/canvas-test", element: <CanvasRoomTestHarness />, requiresAuth: true },
  { path: "/lesson-room-test", element: <LessonRoomTestHarness />, requiresAuth: true },
  { path: "/gig-promo-test", element: <GigPromoRoomTestHarness />, requiresAuth: true },
  { path: "/menu", element: <ArtMenuGenerator />, requiresAuth: true },
  { path: "/profile", element: <Profile />, requiresAuth: true },
  {
    path: "/bring-any-idea",
    element: (
      <ErrorBoundary componentName="BringAnyIdea">
        <BringAnyIdeaPage />
      </ErrorBoundary>
    ),
    requiresAuth: false
  },
  {
    path: "/creative-lab",
    element: (
      <CanvasProvider>
        <ErrorBoundary componentName="CreativeLab">
          <CreativeLab />
        </ErrorBoundary>
      </CanvasProvider>
    ),
    requiresAuth: true
  },
  {
    path: "/radio-challenge",
    element: (
      <ErrorBoundary componentName="RadioChronicleChallenge">
        <RadioChronicleChallenge />
      </ErrorBoundary>
    ),
    requiresAuth: true
  },
  { path: "/preview", element: <TogeeVisionPreview />, requiresAuth: false },
];

function PlaylistSynchronizer() {
  const availableChronicles = useSelector((state) => state.hero.availableChronicles || []);
  const { language } = useLanguage();
  const { setPlaylist } = useAudioPlayerContext();

  useEffect(() => {
    if (availableChronicles.length > 0) {
      const filtered = filterChroniclesByLanguage(availableChronicles, language);
      setPlaylist(filtered);
    }
  }, [availableChronicles, language, setPlaylist]);

  return null;
}

function App() {
  const dispatch = useDispatch();
  const chronicleStatus = useSelector((state) => state.hero.status || 'idle');

  const resolvedApiBase = useMemo(() => resolveApiBaseUrl().replace(/\/$/, ''), []);

  // Fetch initial chronicles globally
  useEffect(() => {
    if (chronicleStatus === 'idle') {
      dispatch(fetchChronicles({ apiBase: resolvedApiBase }));
    }
  }, [chronicleStatus, dispatch, resolvedApiBase]);

  return (
    <AuthProvider>
      <SovereignStateProvider>
        <LanguageProvider>
        <PlaylistSynchronizer />
        <CssBaseline />
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <NavigationGuardProvider>
            <Router future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
              <GlobalNavigationGuard />
              <ScrollManager />
              <OfflineAwareWrapper>
                <SkipNavLink />
                <StoneLayout>
                  <React.Suspense fallback={
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh', bgcolor: '#070707' }}>
                      <CircularProgress sx={{ color: '#D37633' }} />
                    </Box>
                  }>
                    <MatrixRouteBoundary>
                    <Routes>
                    {/* ================================================================= 
                        LEVEL 1: THE TOGEEVISION UNIVERSE (Macro / Narrative Layer)
                       ================================================================= */}
                    <Route path="/" element={<UniverseLayout />}>
                      <Route index element={<ErrorBoundary componentName="HomePage"><SignalLostNotice /><HomePage /></ErrorBoundary>} />
                      <Route path="manifesto" element={<ManifestoPage />} />
                      <Route path="frequency" element={<Navigate to="/manifesto" replace />} />
                      <Route path="sovereign-frequency" element={<Navigate to="/manifesto" replace />} />
                      <Route path="journal" element={
                        <ProtectedRoute>
                          <JournalPage />
                        </ProtectedRoute>
                      } />
                    </Route>

                     {/* ================================================================= 
                        LEVEL 2: THE GIG PASSPORT WORLD (Connection Engine & Sub-Tools)
                       ================================================================= */}
                    <Route 
                      path="/passport" 
                      element={
                        <PassportErrorBoundary>
                          <PassportLayout />
                        </PassportErrorBoundary>
                      }
                    >
                      <Route index element={<GigPassportGateway />} />
                      <Route path="start" element={<GigPassportOnboarding />} />
                      <Route path="app" element={<CanvasWorkspace />} />
                      <Route path="check-in" element={<GigPassport view="signal-check" />} />
                      <Route path="stamps" element={<GigPassport view="passport" />} />
                      <Route path="profile" element={<GigPassport view="passport" />} />
                      <Route path="coordinate" element={<Navigate to="/passport/events" replace />} />
                      <Route path="cities" element={<GigPassport view="cities" />} />
                      <Route path="signal" element={<GigPassport view="signal" />} />
                      <Route path="me" element={<GigPassport view="passport" />} />
                      <Route path="passport" element={<GigPassport view="passport" />} />
                      <Route path="events" element={<GigPassport view="events" />} />
                      <Route path="venue/kismat" element={<GigPassport view="venue" />} />
                      <Route path="signal-check" element={<GigPassport view="signal-check" />} />
                      <Route path="broadcast" element={<GigPassport view="broadcast" />} />
                      <Route path="add-event" element={<GigPassport view="add-event" />} />
                      <Route path="scan/:id" element={<FieldScannerLanding />} />
                      <Route path="staff/:handle" element={<StaffScannerLanding />} />
                      <Route path="radar" element={<SignalRadarFeed />} />
                      <Route path="communications" element={<ErrorBoundary componentName="PassportCommunications"><CommunicationsRouteResolver /></ErrorBoundary>} />
                      <Route path="communications/:mode" element={<ErrorBoundary componentName="PassportCommunications"><Communications /></ErrorBoundary>} />
                      <Route path="communications/*" element={<ErrorBoundary componentName="PassportCommunications"><CommunicationsRouteResolver /></ErrorBoundary>} />
                    </Route>

                    {/* Public archive records use the editorial field shell, not the Passport workspace chrome. */}
                    <Route path="/passport/transmissions/:fieldId" element={<FieldTransmissionPage />} />

                    {/* QR Handshake Routes & Dedicated Field Landing (MAPP MTL) */}
                    <Route path="/q/:code" element={<FieldScannerLanding />} />
                    <Route path="/handshake/:venueSlug" element={<FieldScannerLanding />} />
                    <Route path="/signal/mapp" element={<FieldLandingPage />} />
                    <Route path="/field/montreal" element={<FieldLandingPage />} />
                    <Route path="/signal/:fieldId" element={<FieldLandingPage />} />
                    <Route path="/scan" element={<ScanRoute />} />
                    <Route path="/card" element={<LegacyCardRoute />} />
                    <Route path="/signal-card" element={<LegacyCardRoute />} />
                    <Route path="/qr-card" element={<LegacyCardRoute />} />

                    {/* ================================================================= 
                        LEVEL 3: THE OPERATOR COCKPIT (Control Layer)
                       ================================================================= */}
                    <Route 
                      path="/operator" 
                      element={
                        <ProtectedRoute requiredRole={['admin', 'operator', 'radar_operator']}>
                          <Outlet />
                        </ProtectedRoute>
                      }
                    >
                      <Route index element={<Navigate to="/operator/ledger" replace />} />
                      <Route path="ledger" element={<OperatorLedgerDashboard />} />
                      <Route path="releases" element={<OperatorLedgerDashboard />} />
                      <Route path="audits" element={<OperatorLedgerDashboard />} />
                      <Route path="matrix" element={<OperatorLedgerDashboard />} />
                      <Route path="llm" element={<OperatorLedgerDashboard />} />
                      <Route path="radar" element={<OperatorLedgerDashboard />} />
                      <Route path="signal-field" element={<OperatorLedgerDashboard />} />
                      <Route path="story" element={<OperatorLedgerDashboard />} />
                      <Route path="story/:fragmentId" element={<OperatorLedgerDashboard />} />
                      <Route path="qr" element={<QrGeneratorPage />} />
                      <Route path="qr/ledger" element={<QrGeneratorPage defaultTab="ledger" />} />
                      <Route path="research" element={<OperatorLedgerDashboard />} />
                      <Route path="*" element={<OperatorLedgerDashboard />} />
                    </Route>

                    {/* Redirect Legacy Gig Passport and Admin Routes */}
                    <Route path="/rtm" element={<QueryRedirect to="/passport/radar?city=rtm" />} />
                    <Route path="/admin/cockpit" element={<AdminRedirect toTab="ledger" />} />
                    <Route path="/admin/matrix" element={<Navigate to="/operator/matrix" replace />} />
                    <Route path="/admin/qr" element={<QueryRedirect to="/operator/qr" />} />
                    <Route path="/admin/qr/ledger" element={<QueryRedirect to="/operator/qr/ledger" />} />
                    <Route path="/gig-passport" element={<QueryRedirect to="/passport" />} />
                    <Route path="/gig-passport/app" element={<QueryRedirect to="/passport/app" />} />
                    <Route path="/gig-passport/me" element={<QueryRedirect to="/passport/profile" />} />
                    <Route path="/gig-passport/passport" element={<QueryRedirect to="/passport/stamps" />} />
                    <Route path="/gig-passport/radar" element={<QueryRedirect to="/passport/radar" />} />
                    <Route path="/gig-passport/events" element={<QueryRedirect to="/passport/events" />} />
                    <Route path="/gig-passport/scan/:id" element={<ScanRedirect />} />
                    <Route path="/gig-passport/staff/:handle" element={<StaffRedirect />} />
                    <Route path="/gig-passport/transmissions/:fieldId" element={<TransmissionRedirect />} />
                    <Route path="/gig-passport/*" element={<QueryRedirect to="/passport" />} />

                    {/* Flat Legacy/Standard Routes */}
                    {routes.filter(r => 
                      r.path !== "/" && 
                      r.path !== "/journal" && 
                      !r.path.startsWith("/gig-passport") &&
                      !r.path.startsWith("/passport") &&
                      !r.path.startsWith("/operator") &&
                      r.path !== "/signal-card" &&
                      r.path !== "/card" &&
                      r.path !== "/qr-card" &&
                      r.path !== "/signal/mapp" &&
                      r.path !== "/field/montreal" &&
                      r.path !== "/signal/:fieldId"
                    ).map(({ path, element, requiresAuth, requiredRole }) => (
                      <Route
                        key={path}
                        path={path}
                        element={
                          requiresAuth ? (
                            <ProtectedRoute requiredRole={requiredRole}>
                              {element}
                            </ProtectedRoute>
                          ) : (
                            element
                          )
                        }
                      />
                    ))}
                    <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                    </MatrixRouteBoundary>
                  </React.Suspense>
                  <Footer />
                </StoneLayout>
              </OfflineAwareWrapper>
            </Router>
          </NavigationGuardProvider>
        </LocalizationProvider>
      </LanguageProvider>
      </SovereignStateProvider>
    </AuthProvider>
  );
}

export default App;
